// LocalStorage Service - Handles all data storage operations
// This replaces API calls for local development/demo mode

const STORAGE_KEYS = {
  USERS: 'medicalcrm_users',
  PATIENTS: 'medicalcrm_patients',
  SALES: 'medicalcrm_sales',
  PURCHASES: 'medicalcrm_purchases',
  CREDIT_PURCHASES: 'medicalcrm_credit_purchases',
  DOCTORS: 'medicalcrm_doctors',
  APPOINTMENTS: 'medicalcrm_appointments',
  SETTINGS: 'medicalcrm_settings',
  DASHBOARD_STATS: 'medicalcrm_dashboard_stats',
};

// Helper functions
const getItem = (key, defaultValue = []) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
};

const setItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
    return false;
  }
};

// Patients Service
export const patientsStorage = {
  getAll: () => getItem(STORAGE_KEYS.PATIENTS, []),
  
  getById: (id) => {
    const patients = getItem(STORAGE_KEYS.PATIENTS, []);
    return patients.find(p => p.id === parseInt(id));
  },
  
  create: (patientData) => {
    const patients = getItem(STORAGE_KEYS.PATIENTS, []);
    const newPatient = {
      id: Date.now(), // Simple ID generation
      ...patientData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    patients.push(newPatient);
    setItem(STORAGE_KEYS.PATIENTS, patients);
    return newPatient;
  },
  
  update: (id, patientData) => {
    const patients = getItem(STORAGE_KEYS.PATIENTS, []);
    const index = patients.findIndex(p => p.id === parseInt(id));
    if (index !== -1) {
      patients[index] = {
        ...patients[index],
        ...patientData,
        updated_at: new Date().toISOString(),
      };
      setItem(STORAGE_KEYS.PATIENTS, patients);
      return patients[index];
    }
    return null;
  },
  
  delete: (id) => {
    const patients = getItem(STORAGE_KEYS.PATIENTS, []);
    const filtered = patients.filter(p => p.id !== parseInt(id));
    setItem(STORAGE_KEYS.PATIENTS, filtered);
    return true;
  },
  
  search: (searchTerm) => {
    const patients = getItem(STORAGE_KEYS.PATIENTS, []);
    if (!searchTerm) return patients;
    const term = searchTerm.toLowerCase();
    return patients.filter(p => 
      (p.patient_name || p.patientName || '').toLowerCase().includes(term) ||
      (p.contact_number || p.contactNumber || '').toLowerCase().includes(term) ||
      (p.email || '').toLowerCase().includes(term)
    );
  },
};

// Sales Service
export const salesStorage = {
  getAll: () => getItem(STORAGE_KEYS.SALES, []),
  
  getById: (id) => {
    const sales = getItem(STORAGE_KEYS.SALES, []);
    return sales.find(s => s.id === parseInt(id));
  },
  
  create: (saleData) => {
    const sales = getItem(STORAGE_KEYS.SALES, []);
    const newSale = {
      id: Date.now(),
      ...saleData,
      created_at: new Date().toISOString(),
    };
    sales.push(newSale);
    setItem(STORAGE_KEYS.SALES, sales);
    return newSale;
  },
  
  getTodaySales: () => {
    const sales = getItem(STORAGE_KEYS.SALES, []);
    const today = new Date().toISOString().split('T')[0];
    return sales.filter(s => {
      const saleDate = new Date(s.created_at).toISOString().split('T')[0];
      return saleDate === today;
    });
  },
  
  getMonthlySales: () => {
    const sales = getItem(STORAGE_KEYS.SALES, []);
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return sales.filter(s => {
      const saleDate = new Date(s.created_at);
      return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
    });
  },
};

// Purchases Service
export const purchasesStorage = {
  getAll: () => getItem(STORAGE_KEYS.PURCHASES, []),
  
  getById: (id) => {
    const purchases = getItem(STORAGE_KEYS.PURCHASES, []);
    return purchases.find(p => p.id === parseInt(id));
  },
  
  create: (purchaseData) => {
    const purchases = getItem(STORAGE_KEYS.PURCHASES, []);
    const newPurchase = {
      id: Date.now(),
      ...purchaseData,
      created_at: new Date().toISOString(),
    };
    purchases.push(newPurchase);
    setItem(STORAGE_KEYS.PURCHASES, purchases);
    return newPurchase;
  },
  
  getTodayPurchases: () => {
    const purchases = getItem(STORAGE_KEYS.PURCHASES, []);
    const today = new Date().toISOString().split('T')[0];
    return purchases.filter(p => {
      const purchaseDate = p.date ? new Date(p.date).toISOString().split('T')[0] : new Date(p.created_at).toISOString().split('T')[0];
      return purchaseDate === today;
    });
  },
  
  getMonthlyPurchases: () => {
    const purchases = getItem(STORAGE_KEYS.PURCHASES, []);
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return purchases.filter(p => {
      const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
      return purchaseDate.getMonth() === currentMonth && purchaseDate.getFullYear() === currentYear;
    });
  },
};

// Dashboard Stats Service
export const dashboardStorage = {
  calculateStats: () => {
    const sales = salesStorage.getAll();
    const purchases = purchasesStorage.getAll();
    const patients = patientsStorage.getAll();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    // Helper function to calculate net sales
    const calculateNetSales = (s) => {
      const sellAmount = parseFloat(s.patient_sell_amount || s.patientSellAmount || 0);
      const returnAmount = parseFloat(s.return_amount || s.returnAmount || 0);
      const discount = parseFloat(s.discount || 0);
      return sellAmount - returnAmount - discount;
    };
    
    // Today's sales
    const todaySales = sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        saleDate.setHours(0, 0, 0, 0);
        return saleDate.toISOString().split('T')[0] === todayStr;
      })
      .reduce((sum, s) => sum + calculateNetSales(s), 0);
    
    // Yesterday's sales
    const yesterdaySales = sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        saleDate.setHours(0, 0, 0, 0);
        return saleDate.toISOString().split('T')[0] === yesterdayStr;
      })
      .reduce((sum, s) => sum + calculateNetSales(s), 0);
    
    // Today's purchases
    const todayPurchases = purchases
      .filter(p => {
        const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
        purchaseDate.setHours(0, 0, 0, 0);
        return purchaseDate.toISOString().split('T')[0] === todayStr;
      })
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    // Yesterday's purchases
    const yesterdayPurchases = purchases
      .filter(p => {
        const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
        purchaseDate.setHours(0, 0, 0, 0);
        return purchaseDate.toISOString().split('T')[0] === yesterdayStr;
      })
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    // Today's profit
    const todayProfit = todaySales - todayPurchases;
    const yesterdayProfit = yesterdaySales - yesterdayPurchases;
    
    // Calculate trends
    const salesTrend = yesterdaySales > 0 
      ? ((todaySales - yesterdaySales) / yesterdaySales * 100).toFixed(1)
      : todaySales > 0 ? 100 : 0;
    
    const purchasesTrend = yesterdayPurchases > 0
      ? ((todayPurchases - yesterdayPurchases) / yesterdayPurchases * 100).toFixed(1)
      : todayPurchases > 0 ? 100 : 0;
    
    const profitTrend = yesterdayProfit !== 0
      ? ((todayProfit - yesterdayProfit) / Math.abs(yesterdayProfit) * 100).toFixed(1)
      : todayProfit > 0 ? 100 : 0;
    
    // Monthly calculations
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const lastMonth = new Date(currentYear, currentMonth - 1, 1);
    
    const monthlySales = sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
      })
      .reduce((sum, s) => sum + calculateNetSales(s), 0);
    
    const lastMonthSales = sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        return saleDate.getMonth() === lastMonth.getMonth() && saleDate.getFullYear() === lastMonth.getFullYear();
      })
      .reduce((sum, s) => sum + calculateNetSales(s), 0);
    
    const monthlyPurchases = purchases
      .filter(p => {
        const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
        return purchaseDate.getMonth() === currentMonth && purchaseDate.getFullYear() === currentYear;
      })
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    const lastMonthPurchases = purchases
      .filter(p => {
        const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
        return purchaseDate.getMonth() === lastMonth.getMonth() && purchaseDate.getFullYear() === lastMonth.getFullYear();
      })
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    const monthlyProfit = monthlySales - monthlyPurchases;
    const lastMonthProfit = lastMonthSales - lastMonthPurchases;
    
    const monthlyProfitTrend = lastMonthProfit !== 0
      ? ((monthlyProfit - lastMonthProfit) / Math.abs(lastMonthProfit) * 100).toFixed(1)
      : monthlyProfit > 0 ? 100 : 0;
    
    // Outstanding balances
    const outstandingBalance = sales
      .reduce((sum, s) => sum + parseFloat(s.remaining_amount || s.remainingAmount || 0), 0);
    
    // Admitted patients
    const admittedPatients = patients.filter(p => 
      (p.admit_date || p.admitDate) && !(p.discharge_date || p.dischargeDate)
    ).length;
    
    return {
      todaySales,
      todayPurchases,
      todayProfit,
      monthlyProfit,
      outstandingBalance,
      admittedPatients,
      trends: {
        salesTrend: parseFloat(salesTrend),
        purchasesTrend: parseFloat(purchasesTrend),
        profitTrend: parseFloat(profitTrend),
        monthlyProfitTrend: parseFloat(monthlyProfitTrend),
      },
    };
  },
  
  getCategorySales: () => {
    const sales = salesStorage.getAll();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const categoryMap = {};
    
    sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
      })
      .forEach(s => {
        const patientType = (s.patient_type || s.patientType || 'OPD').toUpperCase();
        const sellAmount = parseFloat(s.patient_sell_amount || s.patientSellAmount || 0);
        const returnAmount = parseFloat(s.return_amount || s.returnAmount || 0);
        const discount = parseFloat(s.discount || 0);
        const netAmount = sellAmount - returnAmount - discount;
        
        if (!categoryMap[patientType]) {
          categoryMap[patientType] = 0;
        }
        categoryMap[patientType] += netAmount;
      });
    
    const total = Object.values(categoryMap).reduce((sum, val) => sum + val, 0);
    
    const categories = Object.entries(categoryMap)
      .map(([name, amount]) => ({
        name,
        amount,
        percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
      }))
      .sort((a, b) => b.amount - a.amount);
    
    // Add default categories if no data
    if (categories.length === 0) {
      return [
        { name: 'OPD', amount: 0, percentage: 0 },
        { name: 'IPD', amount: 0, percentage: 0 },
        { name: 'Others', amount: 0, percentage: 0 },
      ];
    }
    
    return categories;
  },
  
  getTopSellingCategories: () => {
    const sales = salesStorage.getAll();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const categoryMap = {};
    
    sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
      })
      .forEach(s => {
        const patientType = (s.patient_type || s.patientType || 'OPD').toUpperCase();
        const sellAmount = parseFloat(s.patient_sell_amount || s.patientSellAmount || 0);
        const returnAmount = parseFloat(s.return_amount || s.returnAmount || 0);
        const discount = parseFloat(s.discount || 0);
        const netAmount = sellAmount - returnAmount - discount;
        
        if (!categoryMap[patientType]) {
          categoryMap[patientType] = 0;
        }
        categoryMap[patientType] += netAmount;
      });
    
    return Object.entries(categoryMap)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  },
  
  getProfitData: () => {
    const sales = salesStorage.getAll();
    const purchases = purchasesStorage.getAll();
    const now = new Date();
    
    // Last 7 days daily profit
    const dailyProfit = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const dateStr = date.toISOString().split('T')[0];
      
      const daySales = sales
        .filter(s => {
          const saleDate = new Date(s.created_at);
          saleDate.setHours(0, 0, 0, 0);
          return saleDate.toISOString().split('T')[0] === dateStr;
        })
        .reduce((sum, s) => {
          const sellAmount = parseFloat(s.patient_sell_amount || s.patientSellAmount || 0);
          const returnAmount = parseFloat(s.return_amount || s.returnAmount || 0);
          const discount = parseFloat(s.discount || 0);
          return sum + (sellAmount - returnAmount - discount);
        }, 0);
      
      const dayPurchases = purchases
        .filter(p => {
          const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
          purchaseDate.setHours(0, 0, 0, 0);
          return purchaseDate.toISOString().split('T')[0] === dateStr;
        })
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      
      dailyProfit.push({
        date: dateStr,
        profit: daySales - dayPurchases,
        sales: daySales,
        purchases: dayPurchases,
      });
    }
    
    // Last 6 months monthly profit
    const monthlyProfit = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = monthDate.getMonth();
      const year = monthDate.getFullYear();
      
      const monthSales = sales
        .filter(s => {
          const saleDate = new Date(s.created_at);
          return saleDate.getMonth() === month && saleDate.getFullYear() === year;
        })
        .reduce((sum, s) => {
          const sellAmount = parseFloat(s.patient_sell_amount || s.patientSellAmount || 0);
          const returnAmount = parseFloat(s.return_amount || s.returnAmount || 0);
          const discount = parseFloat(s.discount || 0);
          return sum + (sellAmount - returnAmount - discount);
        }, 0);
      
      const monthPurchases = purchases
        .filter(p => {
          const purchaseDate = p.date ? new Date(p.date) : new Date(p.created_at);
          return purchaseDate.getMonth() === month && purchaseDate.getFullYear() === year;
        })
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      
      monthlyProfit.push({
        month: monthDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
        profit: monthSales - monthPurchases,
        sales: monthSales,
        purchases: monthPurchases,
      });
    }
    
    return { dailyProfit, monthlyProfit };
  },
  
  getCashFlow: () => {
    const sales = salesStorage.getAll();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const collected = sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
      })
      .reduce((sum, s) => sum + parseFloat(s.collected_amount || s.collectedAmount || 0), 0);
    
    const remaining = sales
      .filter(s => {
        const saleDate = new Date(s.created_at);
        return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
      })
      .reduce((sum, s) => sum + parseFloat(s.remaining_amount || s.remainingAmount || 0), 0);
    
    return {
      collected,
      remaining,
      total: collected + remaining,
    };
  },
  
  getOutstandingBalances: () => {
    const sales = salesStorage.getAll();
    const outstanding = sales
      .filter(s => parseFloat(s.remaining_amount || s.remainingAmount || 0) > 0)
      .map(s => {
        const saleDate = s.created_at ? new Date(s.created_at) : new Date();
        // Calculate due date (30 days from sale date by default, or use provided due date)
        const dueDate = s.due_date || s.dueDate || (() => {
          const date = new Date(saleDate);
          date.setDate(date.getDate() + 30); // 30 days from sale
          return date.toISOString().split('T')[0];
        })();
        
        return {
          patient: s.patient_name || s.patientName || 'Unknown',
          amount: parseFloat(s.remaining_amount || s.remainingAmount || 0),
          dueDate: dueDate,
          saleDate: saleDate.toISOString().split('T')[0],
          saleId: s.id,
        };
      })
      .sort((a, b) => {
        // Sort by due date (earliest first)
        return new Date(a.dueDate) - new Date(b.dueDate);
      });
    
    return outstanding;
  },
};

// Settings Service
export const settingsStorage = {
  get: () => {
    return getItem(STORAGE_KEYS.SETTINGS, null);
  },
  
  save: (settings) => {
    return setItem(STORAGE_KEYS.SETTINGS, settings);
  },
};

// Users Service
export const usersStorage = {
  getAll: () => getItem(STORAGE_KEYS.USERS, []),
  
  getById: (id) => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    return users.find(u => u.id === parseInt(id));
  },
  
  getByEmail: (email) => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    return users.find(u => u.email.toLowerCase() === email.toLowerCase());
  },
  
  create: (userData) => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    
    // Check if user already exists
    const existing = users.find(u => u.email.toLowerCase() === userData.email.toLowerCase());
    if (existing) {
      throw new Error('User with this email already exists');
    }
    
    const newUser = {
      id: Date.now(),
      email: userData.email,
      password: userData.password, // Store plain text for localStorage (not secure, but for demo)
      name: userData.name,
      role: userData.role || 'staff',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    users.push(newUser);
    setItem(STORAGE_KEYS.USERS, users);
    return newUser;
  },
  
  update: (id, userData) => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    const index = users.findIndex(u => u.id === parseInt(id));
    if (index !== -1) {
      users[index] = {
        ...users[index],
        ...userData,
        updated_at: new Date().toISOString(),
      };
      setItem(STORAGE_KEYS.USERS, users);
      return users[index];
    }
    return null;
  },
  
  delete: (id) => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    const filtered = users.filter(u => u.id !== parseInt(id));
    setItem(STORAGE_KEYS.USERS, filtered);
    return true;
  },
  
  authenticate: (email, password) => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    const user = users.find(u => 
      u.email.toLowerCase() === email.toLowerCase() && 
      u.password === password
    );
    
    if (user) {
      // Return user without password
      const { password: _, ...userWithoutPassword } = user;
      return userWithoutPassword;
    }
    return null;
  },
  
  // Initialize with default admin user if no users exist
  initialize: () => {
    const users = getItem(STORAGE_KEYS.USERS, []);
    if (users.length === 0) {
      const defaultAdmin = {
        id: 1,
        email: 'admin@medicalcrm.com',
        password: 'admin@9975',
        name: 'Admin User',
        role: 'admin',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      users.push(defaultAdmin);
      setItem(STORAGE_KEYS.USERS, users);
      return defaultAdmin;
    } else {
      // Update existing admin user password if it's the old default
      const adminUser = users.find(u => u.email.toLowerCase() === 'admin@medicalcrm.com');
      if (adminUser && adminUser.password === 'admin123') {
        adminUser.password = 'admin@9975';
        adminUser.updated_at = new Date().toISOString();
        setItem(STORAGE_KEYS.USERS, users);
      }
    }
    return null;
  },
};

// Credit Purchases Service
export const creditPurchasesStorage = {
  getAll: () => getItem(STORAGE_KEYS.CREDIT_PURCHASES, []),
  
  create: (purchaseData) => {
    const purchases = getItem(STORAGE_KEYS.CREDIT_PURCHASES, []);
    const newPurchase = {
      id: Date.now(),
      ...purchaseData,
      created_at: new Date().toISOString(),
    };
    purchases.push(newPurchase);
    setItem(STORAGE_KEYS.CREDIT_PURCHASES, purchases);
    return newPurchase;
  },
  
  update: (id, purchaseData) => {
    const purchases = getItem(STORAGE_KEYS.CREDIT_PURCHASES, []);
    const index = purchases.findIndex(p => p.id === parseInt(id));
    if (index !== -1) {
      purchases[index] = { ...purchases[index], ...purchaseData, updated_at: new Date().toISOString() };
      setItem(STORAGE_KEYS.CREDIT_PURCHASES, purchases);
      return purchases[index];
    }
    return null;
  },
  
  delete: (id) => {
    const purchases = getItem(STORAGE_KEYS.CREDIT_PURCHASES, []);
    const filtered = purchases.filter(p => p.id !== parseInt(id));
    setItem(STORAGE_KEYS.CREDIT_PURCHASES, filtered);
    return true;
  },
  
  getRemaining: () => {
    const purchases = getItem(STORAGE_KEYS.CREDIT_PURCHASES, []);
    // Show all credit purchases (where payment_type is 'Credit')
    return purchases.filter(p => {
      const paymentType = (p.payment_type || p.paymentType || '').toLowerCase();
      return paymentType === 'credit';
    });
  },
  
  getAllCreditPurchases: () => {
    const purchases = getItem(STORAGE_KEYS.CREDIT_PURCHASES, []);
    return purchases.filter(p => {
      const paymentType = (p.payment_type || p.paymentType || '').toLowerCase();
      return paymentType === 'credit';
    });
  },
};

// Appointments Service
export const appointmentsStorage = {
  getAll: () => getItem(STORAGE_KEYS.APPOINTMENTS, []),
  
  create: (appointmentData) => {
    const appointments = getItem(STORAGE_KEYS.APPOINTMENTS, []);
    const newAppointment = {
      id: Date.now(),
      ...appointmentData,
      created_at: new Date().toISOString(),
    };
    appointments.push(newAppointment);
    setItem(STORAGE_KEYS.APPOINTMENTS, appointments);
    return newAppointment;
  },
  
  update: (id, appointmentData) => {
    const appointments = getItem(STORAGE_KEYS.APPOINTMENTS, []);
    const index = appointments.findIndex(a => a.id === parseInt(id));
    if (index !== -1) {
      appointments[index] = { ...appointments[index], ...appointmentData, updated_at: new Date().toISOString() };
      setItem(STORAGE_KEYS.APPOINTMENTS, appointments);
      return appointments[index];
    }
    return null;
  },
  
  delete: (id) => {
    const appointments = getItem(STORAGE_KEYS.APPOINTMENTS, []);
    const filtered = appointments.filter(a => a.id !== parseInt(id));
    setItem(STORAGE_KEYS.APPOINTMENTS, filtered);
    return true;
  },
  
  filterByStatus: (status) => {
    const appointments = getItem(STORAGE_KEYS.APPOINTMENTS, []);
    if (status === 'all') return appointments;
    return appointments.filter(a => (a.status || '').toLowerCase() === status.toLowerCase());
  },
};

// Doctors Service
export const doctorsStorage = {
  getAll: () => getItem(STORAGE_KEYS.DOCTORS, []),
  
  create: (doctorData) => {
    const doctors = getItem(STORAGE_KEYS.DOCTORS, []);
    const newDoctor = {
      id: Date.now(),
      ...doctorData,
      created_at: new Date().toISOString(),
    };
    doctors.push(newDoctor);
    setItem(STORAGE_KEYS.DOCTORS, doctors);
    return newDoctor;
  },
  
  update: (id, doctorData) => {
    const doctors = getItem(STORAGE_KEYS.DOCTORS, []);
    const index = doctors.findIndex(d => d.id === parseInt(id));
    if (index !== -1) {
      doctors[index] = { ...doctors[index], ...doctorData, updated_at: new Date().toISOString() };
      setItem(STORAGE_KEYS.DOCTORS, doctors);
      return doctors[index];
    }
    return null;
  },
  
  delete: (id) => {
    const doctors = getItem(STORAGE_KEYS.DOCTORS, []);
    const filtered = doctors.filter(d => d.id !== parseInt(id));
    setItem(STORAGE_KEYS.DOCTORS, filtered);
    return true;
  },
  
  search: (searchTerm) => {
    const doctors = getItem(STORAGE_KEYS.DOCTORS, []);
    if (!searchTerm) return doctors;
    const term = searchTerm.toLowerCase();
    return doctors.filter(d => 
      (d.name || '').toLowerCase().includes(term) ||
      (d.specialization || '').toLowerCase().includes(term)
    );
  },
};

// Reports Service - Generate reports from localStorage data
export const reportsStorage = {
  getOutstandingDues: () => {
    const sales = getItem(STORAGE_KEYS.SALES, []);
    return sales
      .filter(s => parseFloat(s.remaining_amount || s.remainingAmount || 0) > 0)
      .map(s => ({
        patient: s.patient_name || s.patientName,
        amount: parseFloat(s.remaining_amount || s.remainingAmount || 0),
        dueDate: s.created_at ? new Date(s.created_at).toISOString().split('T')[0] : '',
        reason: s.reason_for_remaining || s.reasonForRemaining || 'Outstanding balance',
        responsible: s.responsible_person || s.responsiblePerson || 'N/A',
        status: parseFloat(s.remaining_amount || s.remainingAmount || 0) > 0 ? 'Pending' : 'Paid',
      }));
  },
  
  getSalesReport: (startDate, endDate) => {
    const sales = getItem(STORAGE_KEYS.SALES, []);
    let filtered = sales;
    
    if (startDate || endDate) {
      filtered = sales.filter(s => {
        const saleDate = s.created_at ? new Date(s.created_at).toISOString().split('T')[0] : '';
        if (startDate && saleDate < startDate) return false;
        if (endDate && saleDate > endDate) return false;
        return true;
      });
    }
    
    return filtered;
  },
  
  getPurchaseReport: (startDate, endDate) => {
    const purchases = getItem(STORAGE_KEYS.PURCHASES, []);
    let filtered = purchases;
    
    if (startDate || endDate) {
      filtered = purchases.filter(p => {
        const purchaseDate = p.date || (p.created_at ? new Date(p.created_at).toISOString().split('T')[0] : '');
        if (startDate && purchaseDate < startDate) return false;
        if (endDate && purchaseDate > endDate) return false;
        return true;
      });
    }
    
    return filtered;
  },
  
  getPatientReport: (startDate, endDate) => {
    const patients = getItem(STORAGE_KEYS.PATIENTS, []);
    let filtered = patients;
    
    if (startDate || endDate) {
      filtered = patients.filter(p => {
        const patientDate = p.admit_date || p.admitDate || (p.created_at ? new Date(p.created_at).toISOString().split('T')[0] : '');
        if (startDate && patientDate < startDate) return false;
        if (endDate && patientDate > endDate) return false;
        return true;
      });
    }
    
    return filtered;
  },
};

// Excel Upload History Service
export const excelUploadStorage = {
  getAll: () => getItem('medicalcrm_excel_uploads', []),
  
  create: (uploadData) => {
    const uploads = getItem('medicalcrm_excel_uploads', []);
    const newUpload = {
      id: Date.now(),
      ...uploadData,
      created_at: new Date().toISOString(),
    };
    uploads.push(newUpload);
    setItem('medicalcrm_excel_uploads', uploads);
    return newUpload;
  },
};

// Export all storage keys for reference
export { STORAGE_KEYS };

